/**
 * Логические группы колонок денег по товару — общие для редактора колонок
 * (подразделы категории «Финансы») и для шапки новой таблицы (автогруппы,
 * как у кластеров). Колонки трёх видов:
 *  - `period:money:<показатель>` — за отрезок календаря блока
 *    (lib/period-columns.ts); с 2026-09-19 это единственный вид, который
 *    предлагает редактор;
 *  - `money_<показатель>_<период>` — за фиксированные 7/14/30 дней; остаются
 *    работать на уже собранных страницах;
 *  - по видам услуг Ozon — `period:money:type_<id>` и `money_type_<id>_<период>`:
 *    раскрываются под итогом своей статьи (доставка покупателю, обратная логистика,
 *    прочие услуги, общие расходы — `types` у показателя).
 */

import { accrualTypeLabel } from "@/lib/finance-accrual-type-labels";
import type { MoneyTypeBucket, MoneyTypeInfo } from "@/lib/finance-product-money";
import { MONEY_GROUPS, MONEY_METRICS, MONEY_PERIODS, MONEY_PERIOD_LABELS, moneyMetricColumnKey, parseMoneyMetricColumnKey, type MoneyMetric, type MoneyPeriod } from "@/lib/money-column-metrics";
import { moneyTypePeriodColumnKey, parseMoneyPeriodColumnKey, periodColumnKey } from "@/lib/period-columns";

export { MONEY_PERIODS, MONEY_PERIOD_LABELS, type MoneyPeriod };

/** Группы колонок денег: подпись и показатели группы в порядке списка. */
export const MONEY_COLUMN_GROUPS: ReadonlyArray<{ label: string; metrics: readonly string[] }> = MONEY_GROUPS.map(group => ({
  label: group.label,
  metrics: MONEY_METRICS.filter(metric => metric.group === group.label).map(metric => metric.key),
}));

const METRIC_BY_KEY = new Map(MONEY_METRICS.map(metric => [metric.key, metric]));
const typesUnder = (metric: MoneyMetric | undefined, types: readonly MoneyTypeInfo[]): MoneyTypeInfo[] =>
  metric?.types ? types.filter(type => type.bucket === metric.types) : [];

export function moneyTypeColumnKey(typeId: number, period: MoneyPeriod): string {
  return `money_type_${typeId}_${period}`;
}

/** Разбор ключа колонки по виду услуги; null — это не такая колонка. */
export function parseMoneyTypeColumnKey(key: string): { typeId: number; period: MoneyPeriod } | null {
  const match = /^money_type_(\d+)_(7d|14d|30d)$/.exec(key);
  return match ? { typeId: Number(match[1]), period: match[2] as MoneyPeriod } : null;
}

/** Описание динамической колонки по виду услуги — подпись и подсказка. */
export function getMoneyTypeColDef(key: string): { label: string; description: string; apiField: string } | null {
  const parsed = parseMoneyTypeColumnKey(key);
  if (!parsed) return null;
  const label = accrualTypeLabel(parsed.typeId);
  return {
    label: `${label} ${MONEY_PERIOD_LABELS[parsed.period]}`,
    description: `Услуга Ozon «${label}» (тип начисления ${parsed.typeId}) по этому товару.\nКак считается:\n• Ozon начислил её на товар — сумма напрямую\n• начислена без артикула (общая) — доля товара: складские по проданным штукам, остальные по выручке\nСчитается за последние ${parsed.period.replace("d", "")} дней до вчерашнего дня.`,
    apiField: `finance_product_daily: type:${parsed.typeId}`,
  };
}

/**
 * Ключи колонок группы за фиксированные периоды: показатель со всеми
 * периодами подряд, за итогом статьи — виды услуг этой статьи.
 */
export function moneyGroupColumnKeys(group: { metrics: readonly string[] }, types: readonly MoneyTypeInfo[] = [], period?: MoneyPeriod): string[] {
  const periods = period ? [period] : MONEY_PERIODS;
  const out: string[] = [];
  for (const metricKey of group.metrics) {
    const metric = METRIC_BY_KEY.get(metricKey);
    for (const p of periods) out.push(moneyMetricColumnKey(metricKey, p));
    for (const type of typesUnder(metric, types)) for (const p of periods) out.push(moneyTypeColumnKey(type.id, p));
  }
  return out;
}

/** Ключи колонок группы за отрезок календаря блока: показатель, за итогом статьи — виды услуг. */
export function moneyGroupPeriodColumnKeys(group: { metrics: readonly string[] }, types: readonly MoneyTypeInfo[] = []): string[] {
  const out: string[] = [];
  for (const metricKey of group.metrics) {
    out.push(periodColumnKey("money", metricKey));
    for (const type of typesUnder(METRIC_BY_KEY.get(metricKey), types)) out.push(moneyTypePeriodColumnKey(type.id));
  }
  return out;
}

/**
 * Подпись денежной колонки без периода: «Начислено Ozon 7д, ₽» → «Начислено
 * Ozon, ₽». Для шапки новой таблицы, где период уже написан в полосе
 * автогруппы над колонкой («Начислено Ozon 7д») — как у кластерных колонок,
 * где кластер показывает полоса, а колонка — только метрику. null — не деньги.
 */
export function moneyColumnLabelWithoutPeriod(key: string, fullLabel: string): string | null {
  const period = moneyColumnPeriod(key);
  if (!period) return null;
  return fullLabel.replace(` ${MONEY_PERIOD_LABELS[period]}`, "");
}

/** Период колонки денег (money_* и money_type_*); null — легаси fin_* и всё остальное. */
export function moneyColumnPeriod(key: string): MoneyPeriod | null {
  return parseMoneyTypeColumnKey(key)?.period ?? parseMoneyMetricColumnKey(key)?.period ?? null;
}

/** Старые денежные колонки по начислениям за 30 дней (fin_*) — своя группа в шапке. */
export const LEGACY_MONEY_COLUMNS = new Set(["fin_revenue", "fin_commission", "fin_logistics", "fin_last_mile", "fin_fulfillment", "fin_mainline", "fin_return_amount"]);
export const LEGACY_MONEY_GROUP_LABEL = "Начисления 30д";

/** Группа, под итогом которой раскрывается вид услуги с таким bucket. */
function groupLabelOfBucket(bucket: MoneyTypeBucket | undefined): string | null {
  const metric = bucket ? MONEY_METRICS.find(item => item.types === bucket) : undefined;
  return metric?.group ?? null;
}

/**
 * Группа колонки денег (итог, вид услуги, «за период» или старая колонка) —
 * подпись и период; у колонок за отрезок календаря периода нет; null — не
 * колонка денег.
 */
export function moneyColumnGroupOf(key: string, types: readonly MoneyTypeInfo[] = []): { label: string; period?: MoneyPeriod } | null {
  if (LEGACY_MONEY_COLUMNS.has(key)) return { label: LEGACY_MONEY_GROUP_LABEL };
  const period = parseMoneyPeriodColumnKey(key);
  if (period) {
    if (period.typeId != null) {
      const label = groupLabelOfBucket(types.find(item => item.id === period.typeId)?.bucket);
      return label ? { label } : null;
    }
    const metric = METRIC_BY_KEY.get(period.metric);
    return metric ? { label: metric.group } : null;
  }
  const type = parseMoneyTypeColumnKey(key);
  if (type) {
    const label = groupLabelOfBucket(types.find(item => item.id === type.typeId)?.bucket);
    return label ? { label, period: type.period } : null;
  }
  const parsed = parseMoneyMetricColumnKey(key);
  return parsed ? { label: parsed.metric.group, period: parsed.period } : null;
}
